import { Button, Checkbox, FormControlLabel, InputAdornment, makeStyles, TextField } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useAuthUser } from "Components/Auth/hooks/useAuthUser";
import { useComputedCompany } from "Components/Company/hooks/useComputedCompany";
import { ValidMember, displayEmployeeName } from "Components/Members/MembersHelper";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { getIsDayViewEnabled } from "Components/ProfileLimit/helpers/getIsDayViewEnabled";
import { useProfileLimits } from "Components/ProfileLimit/hooks/useProfileLimits";
import { px } from "Configs/theme";
import {
	IsCalendarSubHeadOpen,
	SetIsCalendarSubHeadOpen,
	LocalStorageKey,
} from "Helpers/LocalStorageHelper";
import { useIsBrowserOrIpadOs } from "Helpers/PlatformHelper";
import { useRedirect } from "Helpers/RedirectHelper";
import { useTranslation } from "Providers/TranslationProvider";
import React, { FC, Fragment, useState, useEffect } from "react";
import { useScheduleEntries } from "Components/ScheduleEntry/hooks/useScheduleEntries";
import CalendarSubHeadRow from "./CalendarSubHeadRow";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { CalendarType } from "Components/Navigation/NavigationHelper";
import moment from "moment";

interface Props {
	scheduleGroupMemberIds: string[];
	isCalendarSubHeadOpen: IsCalendarSubHeadOpen;
	setIsCalendarSubHeadOpen: SetIsCalendarSubHeadOpen;
	renderMemberRow: (member: ValidMember) => JSX.Element;
}

const CalendarMemberRows: FC<Props> = (props) => {
	const { t } = useTranslation();
	const isBrowserOrIpadOs = useIsBrowserOrIpadOs();
	const classes = useStyles();
	const redirect = useRedirect();
	const isInternalAdmin = useIsInternalAdmin();
	const userId = useAuthUser((state) => state.user?.uid);
	const profileLimits = useProfileLimits((state) => state.items);
	const isDayViewEnabled = userId && getIsDayViewEnabled(userId, profileLimits);
	const validMembers = useComputedCompany((state) => state.validMembers);
	const scheduleEntries = useScheduleEntries((state) => state.items);
	const calendarType = useCalendar(state => state.calendarType);
	const selectedDate = useCalendar(state => state.selectedDate);
	const startOfWeek = useCalendar(state => state.startOfWeek);
	
	const [hideUnscheduled, setHideUnscheduled] = useState<boolean>(() => {
		const storedValue = localStorage.getItem(LocalStorageKey.SHOW_ONLY_SCHEDULED_MEMBERS);
		return storedValue ? JSON.parse(storedValue) : false;
	});
	const [searchQuery, setSearchQuery] = useState("");

	useEffect(() => {
		localStorage.setItem(LocalStorageKey.SHOW_ONLY_SCHEDULED_MEMBERS, JSON.stringify(hideUnscheduled));
	}, [hideUnscheduled]);

	function getMembersWithScheduleStatus() {
		const scheduledMemberIds = new Set<string>();
		
		// Filter entries based on calendar view type
		const relevantEntries = scheduleEntries.filter(entry => {
			const startTime = moment.unix(entry.startTime);
			const endTime = moment.unix(entry.endTime);
			
			if (calendarType === CalendarType.DAY) {
				// For day view: entry is on the selected day
				return startTime.isSame(selectedDate, 'day') || 
				       endTime.isSame(selectedDate, 'day') || 
				       (startTime.isBefore(selectedDate, 'day') && endTime.isAfter(selectedDate, 'day'));
			} else if (calendarType === CalendarType.WEEK) {
				// For week view: entry is in the current week (based on startOfWeek)
				const endOfWeek = startOfWeek.clone().add(6, 'days').endOf('day');
				return (startTime.isSameOrAfter(startOfWeek) && startTime.isSameOrBefore(endOfWeek)) || 
				       (endTime.isSameOrAfter(startOfWeek) && endTime.isSameOrBefore(endOfWeek)) ||
				       (startTime.isBefore(startOfWeek) && endTime.isAfter(endOfWeek));
			} else {
				// For month view: entry is in the current month
				const startOfMonth = selectedDate.clone().startOf('month');
				const endOfMonth = selectedDate.clone().endOf('month');
				return (startTime.isSameOrAfter(startOfMonth) && startTime.isSameOrBefore(endOfMonth)) || 
				       (endTime.isSameOrAfter(startOfMonth) && endTime.isSameOrBefore(endOfMonth)) ||
				       (startTime.isBefore(startOfMonth) && endTime.isAfter(endOfMonth));
			}
		});
		
		// Get unique member IDs from filtered entries
		relevantEntries.forEach(entry => {
			if (entry.userId) {
				scheduledMemberIds.add(entry.userId);
			}
		});
		
		return validMembers
			.filter(member => !props.scheduleGroupMemberIds.includes(member.id))
			.map(member => ({
				member,
				isScheduled: scheduledMemberIds.has(member.id)
			}));
	}
	
	function renderRows() {
		const membersWithStatus = getMembersWithScheduleStatus();
		
		// Apply hide unscheduled filter first if enabled
		const filteredBySchedule = hideUnscheduled 
			? membersWithStatus.filter(({ isScheduled }) => isScheduled)
			: membersWithStatus;
		
		// Then apply search filter if it exists
		const searchLower = searchQuery.toLowerCase();
		const finalFiltered = searchLower
			? filteredBySchedule.filter(({ member }) => {
				const memberName = displayEmployeeName(member).toLowerCase();
				return memberName.includes(searchLower);
			})
			: filteredBySchedule;
			
		return finalFiltered.map(({ member }) => props.renderMemberRow(member));
	}

	const titleWithSearch = (
		<div className={classes.titleContainer}>
			<span>{t("CalenderMemberRows_Title")}</span>
			{isDayViewEnabled && (
				<TextField
					placeholder={t("Common_Search") || "Search"}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					size="small"
					className={classes.searchField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
						className: classes.searchInput
					}}
				/>
			)}
		</div>
	);

	return (
		<Fragment>
			<CalendarSubHeadRow
				title=""
				customTitle={titleWithSearch}
				isOpen={props.isCalendarSubHeadOpen.employees}
				setIsOpen={(isOpen) =>
					props.setIsCalendarSubHeadOpen("employees", isOpen)
				}
			>
				<div className={classes.headerControls}>
					{isDayViewEnabled && (
						<FormControlLabel
							control={
								<Checkbox
									checked={hideUnscheduled}
									onChange={(e) => setHideUnscheduled(e.target.checked)}
									size="small"
									color="primary"
								/>
							}
							label={t("Calendar_HideUnscheduled") || "Hide Unscheduled"}
							className={classes.checkbox}
							classes={{
								label: classes.checkboxLabel
							}}
						/>
					)}
					{isInternalAdmin && isBrowserOrIpadOs && (
						<Button
							className={classes.button}
							color="primary"
							size="small"
							onClick={() => redirect("settings/company-employees")}
						>
							+ {t("CalenderMemberRows_Button_AddMember")}
						</Button>
					)}
				</div>
			</CalendarSubHeadRow>
			{props.isCalendarSubHeadOpen.employees && renderRows()}
		</Fragment>
	);
};

export default CalendarMemberRows;

const useStyles = makeStyles({
	titleContainer: {
		display: 'flex',
		alignItems: 'center',
		'& > span': {
			marginRight: px.m,
		}
	},
	headerControls: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		marginLeft: 'auto',
	},
	button: {
		marginRight: px.l,
		marginLeft: px.m,
		whiteSpace: 'nowrap',
	},
	searchField: {
		width: 180,
	},
	searchInput: {
		fontSize: '12px',
		height: '32px',
	},
	checkbox: {
		marginLeft: 0,
		fontSize: 12,
		whiteSpace: 'nowrap',
	},
	checkboxLabel: {
		textTransform: 'none',
		fontSize: '12px'
	},
});
